import { Table, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FaEye, FaEdit, FaClock } from "react-icons/fa";
import {
  HourglassSplit,
  SendFill,
  Search,
  ArrowClockwise,
  CheckCircleFill,
  XCircleFill,
  ExclamationCircle,
} from "react-bootstrap-icons";
import { useState } from "react";
import { getSindicatoById } from "../../../../../services";

const normalize = (text: string) =>
  text
    ? text
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim()
    : "";

const isAdequacaoPendente = (situacaoRaw: string) =>
  normalize(situacaoRaw).includes("adequacao pendente");

const hasSolicitacaoAdequacao = (solicitacao: string | null | undefined) => {
  if (solicitacao == null || String(solicitacao).trim() === "") return false;
  const t = new Date(solicitacao).getTime();
  return !Number.isNaN(t);
};

const getDiasCorridosDesdeSolicitacao = (solicitacao: string) => {
  const solic = new Date(solicitacao);
  if (Number.isNaN(solic.getTime())) return null;
  const hoje = new Date();
  const utcSolic = Date.UTC(
    solic.getFullYear(),
    solic.getMonth(),
    solic.getDate(),
  );
  const utcHoje = Date.UTC(
    hoje.getFullYear(),
    hoje.getMonth(),
    hoje.getDate(),
  );
  const diff = Math.floor((utcHoje - utcSolic) / (1000 * 60 * 60 * 24));
  return diff < 0 ? 0 : diff;
};

const textoTooltipDiasAdequacao = (dias: number | null) => {
  const n = dias ?? 0;
  const unidade = n === 1 ? "dia corrido" : "dias corridos";
  return `Há ${n} ${unidade} desde a solicitação de adequação.`;
};

const getSituationConfig = (situacaoRaw: string) => {
  const situacao = normalize(situacaoRaw);

  if (situacao.includes("documentacao pendente")) {
    return { icon: <HourglassSplit />, className: "status-pendente-sindicato" };
  }
  if (situacao.includes("documentacao enviada")) {
    return { icon: <SendFill />, className: "status-enviado" };
  }
  if (situacao.includes("adequacao pendente")) {
    return { icon: <HourglassSplit />, className: "status-adequacao-recusa" };
  }
  if (situacao.includes("analise pendente")) {
    return { icon: <Search />, className: "status-pendente-analista" };
  }
  if (situacao.includes("analise em andamento")) {
    return { icon: <ArrowClockwise />, className: "status-andamento" };
  }
  if (situacao.includes("analise concluida")) {
    return { icon: <CheckCircleFill color="#067647" />, className: "status-plain" };
  }
  if (situacao.includes("inapto")) {
    return { icon: <XCircleFill color="#BA0C2F" />, className: "status-plain" };
  }

  return { icon: <ExclamationCircle />, className: "status-default" };
};

const formatCNPJ = (cnpj: string | null | undefined) => {
  if (!cnpj || String(cnpj).replace(/\D/g, "").length !== 14) return cnpj;
  const d = String(cnpj).replace(/\D/g, "");
  return d.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    "$1.$2.$3/$4-$5",
  );
};

const renderSortableTitle = (
  titulo: string,
  key: string,
  getSortIcon: (key: string) => string,
) => {
  const partes = String(titulo).trim().split(/\s+/);
  const empilharDuasLinhas = partes.length === 2;

  return (
    <span
      className={`analysis-sort-title${
        empilharDuasLinhas ? " analysis-sort-title--stacked" : ""
      }`}
    >
      {empilharDuasLinhas ? (
        <span className="analysis-sort-text">
          <span>{partes[0]}</span>
          <span>{partes[1]}</span>
        </span>
      ) : (
        <span>{titulo}</span>
      )}
      <span className="analysis-sort-icon">{getSortIcon(key)}</span>
    </span>
  );
};

export type ResultTableItem = {
  id?: number;
  cnpj?: string | null;
  sigla?: string | null;
  
  solicitacao?: string | null;
  situacao?: string;
  envioDocumentacao?: string | null;
  ultimaAtualizacao?: string | null;
  solicitacaoAdequacao?: string | null;
  adequacaoDocumentacao?: string | null;
  conclusaoAnalise?: string | null;
  dataConclusaoAnalise?: string | null;
  idQuestionarioResposta?: number | null;
  idSindicato?: number | null;
  
  envio?: string | null;
  atualizacao?: string | null;
  adequacao?: string | null;
  questionario?: number;
  rowKey?: string;
};

const pareceDataIso = (v: string | null | undefined) =>
  typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v);

type ResultTableProps = {
  currentItems: ResultTableItem[];
  handleSort: (key: string) => void;
  getSortIcon: (key: string) => string;
  onOpenHistory: (item: ResultTableItem) => void;
  onView: (item: ResultTableItem) => void;
  formatDate: (date: string | null | undefined) => string;
  
  modoListaSindicatos?: boolean;
  
  analisePorSolicitacaoAvaliacao?: boolean;
};

const resolveAnaliseFlags = (
  item: ResultTableItem,
  analisePorSolicitacaoAvaliacao: boolean,
) => {
  const idResposta = item.idQuestionarioResposta ?? item.questionario;
  const idSindicatoAlvo = analisePorSolicitacaoAvaliacao
    ? item.idSindicato ?? undefined
    : item.idSindicato ?? item.id;
  const podeAnalisarBase = analisePorSolicitacaoAvaliacao
    ? !!idResposta
    : !!idResposta && !!idSindicatoAlvo;
  return { idResposta, idSindicatoAlvo, podeAnalisarBase };
};

const ResultTable = ({
  currentItems,
  handleSort,
  getSortIcon,
  onOpenHistory,
  onView,
  formatDate,
  modoListaSindicatos = true,
  analisePorSolicitacaoAvaliacao = false,
}: ResultTableProps) => {
  const [tooltipContent, setTooltipContent] = useState("");

  const handleTooltip = async (id: number | undefined) => {
    if (id == null) return;
    try {
      const response = await getSindicatoById(id);
      setTooltipContent(response.data.nome);
    } catch {
      setTooltipContent("Erro ao carregar nome");
    }
  };

  if (!modoListaSindicatos) {
    return (
      <div className="analysis-table-scroll">
        <Table bordered hover className="analysis-table">
          <thead>
            <tr>
              <th
                onClick={() => handleSort("solicitacao")}
                className="sortable-header"
              >
                {renderSortableTitle("Solicitação", "solicitacao", getSortIcon)}
              </th>
              <th
                onClick={() => handleSort("situacao")}
                className="sortable-header"
              >
                {renderSortableTitle("Situação", "situacao", getSortIcon)}
              </th>
              <th
                onClick={() => handleSort("envioDocumentacao")}
                className="sortable-header"
              >
                {renderSortableTitle(
                  "Envio Documentação",
                  "envioDocumentacao",
                  getSortIcon,
                )}
              </th>
              <th
                onClick={() => handleSort("ultimaAtualizacao")}
                className="sortable-header"
              >
                {renderSortableTitle(
                  "Última Atualização",
                  "ultimaAtualizacao",
                  getSortIcon,
                )}
              </th>
              <th
                onClick={() => handleSort("solicitacaoAdequacao")}
                className="sortable-header"
              >
                {renderSortableTitle(
                  "Solicitação Adequação",
                  "solicitacaoAdequacao",
                  getSortIcon,
                )}
              </th>
              <th
                onClick={() => handleSort("adequacaoDocumentacao")}
                className="sortable-header"
              >
                {renderSortableTitle(
                  "Adequação Documentação",
                  "adequacaoDocumentacao",
                  getSortIcon,
                )}
              </th>
              <th
                onClick={() => handleSort("conclusaoAnalise")}
                className="sortable-header"
              >
                {renderSortableTitle(
                  "Conclusão Análise",
                  "conclusaoAnalise",
                  getSortIcon,
                )}
              </th>
              <th className="text-center">Ações</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.map((item, index) => {
              const { icon, className } = getSituationConfig(
                item.situacao || "",
              );
              const { podeAnalisarBase } = resolveAnaliseFlags(
                item,
                analisePorSolicitacaoAvaliacao,
              );

              return (
                <tr key={`${item.rowKey ?? item.id ?? "row"}-${index}`}>
                  <td>
                    {pareceDataIso(item.solicitacao)
                      ? "1ª Solicitação"
                      : item.solicitacao ?? "—"}
                  </td>
                  <td>
                    <div className="situation-cell">
                      <span className={`situation-icon ${className}`}>
                        {icon}
                      </span>
                      <span className="situation-text">{item.situacao}</span>
                    </div>
                  </td>
                  <td>
                    {formatDate(item.envioDocumentacao ?? item.envio ?? null)}
                  </td>
                  <td>
                    {formatDate(
                      item.ultimaAtualizacao ?? item.atualizacao ?? null,
                    )}
                  </td>
                  <td>
                    {formatDate(
                      item.solicitacaoAdequacao ??
                        (pareceDataIso(item.solicitacao)
                          ? item.solicitacao
                          : null),
                    )}
                  </td>
                  <td>
                    {formatDate(
                      item.adequacaoDocumentacao ?? item.adequacao ?? null,
                    )}
                  </td>
                  <td>
                    {formatDate(
                      item.conclusaoAnalise ?? item.dataConclusaoAnalise ?? null,
                    )}
                  </td>
                  <td>
                    <div className="action-buttons d-flex gap-2 justify-content-center">
                      <Button
                        className="btn-edit"
                        size="sm"
                        onClick={() => onView(item)}
                        disabled={!podeAnalisarBase}
                        title={podeAnalisarBase ? "Analisar" : "Visualizar"}
                      >
                        {podeAnalisarBase ? <FaEdit /> : <FaEye />}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onOpenHistory(item)}
                        title="Ver histórico"
                      >
                        <FaClock />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  }

  return (
    <div className="analysis-table-scroll">
      <Table bordered hover className="analysis-table">
        <thead>
          <tr>
            <th
              onClick={() => handleSort("cnpj")}
              className="sortable-header"
            >
              {renderSortableTitle("SINDICATO", "cnpj", getSortIcon)}
            </th>
            <th
              onClick={() => handleSort("solicitacao")}
              className="sortable-header"
            >
              {renderSortableTitle("Solicitação", "solicitacao", getSortIcon)}
            </th>
            <th
              onClick={() => handleSort("situacao")}
              className="sortable-header"
            >
              {renderSortableTitle("Situação", "situacao", getSortIcon)}
            </th>
            <th
              onClick={() => handleSort("envioDocumentacao")}
              className="sortable-header"
            >
              {renderSortableTitle(
                "Envio Documentação",
                "envioDocumentacao",
                getSortIcon,
              )}
            </th>
            <th
              onClick={() => handleSort("ultimaAtualizacao")}
              className="sortable-header"
            >
              {renderSortableTitle(
                "Última Atualização",
                "ultimaAtualizacao",
                getSortIcon,
              )}
            </th>
            <th
              onClick={() => handleSort("solicitacaoAdequacao")}
              className="sortable-header"
            >
              {renderSortableTitle(
                "Solicitação Adequação",
                "solicitacaoAdequacao",
                getSortIcon,
              )}
            </th>
            <th
              onClick={() => handleSort("adequacaoDocumentacao")}
              className="sortable-header"
            >
              {renderSortableTitle(
                "Adequação Documentação",
                "adequacaoDocumentacao",
                getSortIcon,
              )}
            </th>
            <th
              onClick={() => handleSort("conclusaoAnalise")}
              className="sortable-header"
            >
              {renderSortableTitle(
                "Conclusão Análise",
                "conclusaoAnalise",
                getSortIcon,
              )}
            </th>
            <th className="text-center">Ações</th>
          </tr>
        </thead>

        <tbody>
          {currentItems.map((item, index) => {
            const situacaoTexto = item.situacao || "";
            const { icon, className } = getSituationConfig(situacaoTexto);

            const envioDoc = item.envioDocumentacao ?? item.envio ?? null;
            const ultima = item.ultimaAtualizacao ?? item.atualizacao ?? null;
            const adequacao = item.adequacaoDocumentacao ?? item.adequacao;
            const conclusao =
              item.conclusaoAnalise ?? item.dataConclusaoAnalise;

            const { idSindicatoAlvo, podeAnalisarBase } = resolveAnaliseFlags(
              item,
              analisePorSolicitacaoAvaliacao,
            );
            const podeAnalisar = podeAnalisarBase;
            const dateMatch = envioDoc == null;

            const dataSolAdequacao =
              item.solicitacaoAdequacao ??
              (pareceDataIso(item.solicitacao) ? item.solicitacao : null);

            const solicitacaoCol = pareceDataIso(item.solicitacao)
              ? "1ª Solicitação"
              : item.solicitacao && String(item.solicitacao).trim() !== ""
                ? String(item.solicitacao)
                : "—";
            const mostrarTooltipDias =
              isAdequacaoPendente(situacaoTexto) &&
              hasSolicitacaoAdequacao(dataSolAdequacao);
            const diasCorridos = mostrarTooltipDias
              ? getDiasCorridosDesdeSolicitacao(dataSolAdequacao || "")
              : null;
            const comTooltipDias =
              mostrarTooltipDias && diasCorridos !== null;
            const conteudoCelulaSituacao = (
              <div
                className={
                  comTooltipDias
                    ? "situation-cell situation-cell--tooltip-dias"
                    : "situation-cell"
                }
              >
                <span className={`situation-icon ${className}`}>{icon}</span>
                <span className="situation-text">{situacaoTexto}</span>
              </div>
            );

            return (
              <tr key={`${item.rowKey ?? item.id ?? "row"}-${index}`}>
                <td>
                  <div className="sindicato-cell">
                    <span className="sindicato-cnpj">{formatCNPJ(item.cnpj) || "—"}</span>
                    {idSindicatoAlvo ? (
                      <OverlayTrigger
                        placement="top"
                        delay={{ show: 250, hide: 400 }}
                        onEnter={() => handleTooltip(idSindicatoAlvo)}
                        overlay={(props) => (
                          <Tooltip id="button-tooltip" {...props}>
                            {tooltipContent || "Carregando..."}
                          </Tooltip>
                        )}
                      >
                        <span className="sindicato-sigla">{item.sigla || "—"}</span>
                      </OverlayTrigger>
                    ) : (
                      <span className="sindicato-sigla">{item.sigla || "—"}</span>
                    )}
                  </div>
                </td>
                <td>{solicitacaoCol}</td>
                <td>
                  {comTooltipDias ? (
                    <OverlayTrigger
                      placement="top"
                      delay={{ show: 250, hide: 400 }}
                      overlay={(props) => (
                        <Tooltip
                          id={`tooltip-dias-adequacao-${item.id}-${index}`}
                          {...props}
                        >
                          {textoTooltipDiasAdequacao(diasCorridos)}
                        </Tooltip>
                      )}
                    >
                      {conteudoCelulaSituacao}
                    </OverlayTrigger>
                  ) : (
                    conteudoCelulaSituacao
                  )}
                </td>
                <td>{formatDate(envioDoc)}</td>
                <td>{formatDate(ultima)}</td>
                <td>{formatDate(dataSolAdequacao)}</td>
                <td>{formatDate(adequacao)}</td>
                <td>{formatDate(conclusao)}</td>
                <td>
                  <div className="action-buttons d-flex gap-2 justify-content-center">
                    <Button
                      className="btn-edit"
                      size="sm"
                      onClick={() => onView(item)}
                      disabled={!podeAnalisar || dateMatch}
                      style={
                        !podeAnalisar || dateMatch
                          ? { opacity: 0.2, cursor: "not-allowed" }
                          : {}
                      }
                      title={
                        !podeAnalisar
                          ? "Ainda não iniciado"
                          : dateMatch
                            ? "Sem envio de documentação"
                            : "Analisar"
                      }
                    >
                      {podeAnalisar && !dateMatch ? <FaEdit /> : <FaEye />}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onOpenHistory(item)}
                      title="Ver histórico"
                    >
                      <FaClock />
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default ResultTable;
